const _excluded = ["itemToString", "items", "groupedItems", "isItemDisabled", "target", "layoutDependency", "getMenuProps"],
  _excluded2 = ["isOpen", "getItemProps", "selectItem", "getMenuProps"],
  _excluded3 = ["show", "targetProps", "overlayProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { useCallback, useLayoutEffect, useMemo } from 'react';
import { useSelect } from 'downshift';
import { mergeRefs, useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';
import useGroupedItems from './useGroupedItems';
import { checkReferenceEquality, defaultIsItemDisabled, defaultLabelGetter, identityFn, isGroupHeader, wrapGetItemProps } from '../helpers';
/**
 * A hook used to manage all logic needed for a Dropdown.
 */
const useDropdown = _ref => {
  var _targetRef$current;
  let {
      itemToString = item => defaultLabelGetter(item),
      items,
      groupedItems,
      isItemDisabled = defaultIsItemDisabled,
      target,
      layoutDependency,
      getMenuProps: getMenuPropsProp
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality(_objectSpread(_objectSpread({}, props), {}, {
    items: maybeGroupedItems
  }));
  const targetRef = useForwardedRef(target || null);
  const {
    flatItems,
    indexGetter,
    getMenuItems
  } = useGroupedItems({
    maybeGroupedItems
  });
  const _useSelect = useSelect(_objectSpread({
      itemToString,
      items: flatItems
    }, props)),
    {
      isOpen: downshiftIsMenuOpen,
      getItemProps: rawGetItemProps,
      selectItem,
      getMenuProps: rawGetMenuProps
    } = _useSelect,
    useSelectReturnValue = _objectWithoutProperties(_useSelect, _excluded2);
  const _useOverlay = useOverlay(_objectSpread({
      placement: 'bottom-start',
      show: downshiftIsMenuOpen,
      target: targetRef,
      layoutDependency,
      isOverlayConditionallyRendered: false
    }, props)),
    {
      show: useOverlayIsOpen,
      targetProps,
      overlayProps
    } = _useOverlay,
    useOverlayReturnValue = _objectWithoutProperties(_useOverlay, _excluded3);
  const getMenuProps = useCallback(args => rawGetMenuProps((getMenuPropsProp || identityFn)(_objectSpread(_objectSpread({}, overlayProps), args))), [getMenuPropsProp, overlayProps, rawGetMenuProps]);
  const menuItems = useMemo(() => getMenuItems(flatItems), [flatItems, getMenuItems]);
  const defaultIndexGetter = useCallback(item => menuItems.indexOf(item), [menuItems]);
  const getItemProps = useCallback(args => wrapGetItemProps({
    getItemProps: rawGetItemProps,
    isItemDisabled,
    highlightedIndex: useSelectReturnValue.highlightedIndex,
    selectedItem: useSelectReturnValue.selectedItem,
    indexGetter: indexGetter || defaultIndexGetter
  })(args), [defaultIndexGetter, indexGetter, isItemDisabled, rawGetItemProps, useSelectReturnValue.highlightedIndex, useSelectReturnValue.selectedItem]);
  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);
  return _objectSpread(_objectSpread({
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    isOpen: useOverlayIsOpen,
    /**
     * @deprecated use value returned from menuProps
     */
    menuItems,
    /**
     * @deprecated use value returned from menuProps
     */
    getItemProps,
    /**
     * @deprecated use value returned from menuProps
     */
    getMenuProps,
    controlProps: {
      ref: targetRef,
      isOpen: useOverlayIsOpen,
      getToggleButtonProps: useSelectReturnValue.getToggleButtonProps,
      openMenu: useSelectReturnValue.openMenu,
      selectedItem: useSelectReturnValue.selectedItem,
      itemToString,
      selectItem: selectItem
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex: useSelectReturnValue.highlightedIndex,
      selectedItem: useSelectReturnValue.selectedItem,
      width: (_targetRef$current = targetRef.current) === null || _targetRef$current === void 0 ? void 0 : _targetRef$current.offsetWidth,
      selectItem,
      isGroupHeader
    },
    selectItem
  }, useSelectReturnValue), {}, {
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    targetProps: {
      ref: targetRef
    }
  }, useOverlayReturnValue);
};
export default Object.assign(useDropdown, {
  stateChangeTypes: useSelect.stateChangeTypes
});
//# sourceMappingURL=useDropdown.js.map